/**
 * Handle Application state based on new React Context API,
 * Fully Inspired by unstated but working only with new React Context
 * with few HOC helpers.
 */
import React from 'react';
import PropTypes from 'prop-types';

const { Component } = React;
const StoreContext = React.createContext(null);

class Container {
  /** State with the same shape like React state */
  state = {};

  /** Array of Functions */
  _listeners = [];

  /**
   * setState() mimics React's setState() method as closely as possible.
   * It's also run asynchronously, so you need to follow the same rules as React.
   *
   * However, unlike React's setState(). This setState() returns a promise,
   * so you can await it.
   * Example:
   *
   * class CounterContainer extends Container {
   *  state = { count: 0 };
   *  increment = async () => {
   *    await this.setState({ count: 1 });
   *    console.log(this.state.count); // 1
   *  };
   * }
   *
   * @param {State | (prevState) => State} updater
   * Same like React state with object form or function form.
   *
   * @param {Function} callback simple callback () => void
   *
   * @return {Promise} returns a Promise
   */
  setState(updater, callback) {
    return Promise.resolve().then(() => {
      let nextState;

      if(typeof updater === 'function') {
        nextState = updater(this.state);
      }
      else {
        nextState = updater;
      }

      if(nextState == null) {
        if(callback) callback();
        return;
      }

      this.state = Object.assign({}, this.state, nextState);

      let promises = this._listeners.map((listener) => listener());

      return Promise.all(promises).then(() => {
        if(callback) {
          return callback();
        }
      });
    });
  }

  /**
   * Subscribe to Listening on container state changes.
   * This used in Subscribe component.
   *
   * @param {Function} fn function to subscribe
   */
  subscribe(fn) {
    this._listeners.push(fn);
  }

  /**
   * Unsubscribe from Listening on container state changes.
   * This is used in Subscribe component.
   *
   * @param {Function} fn function with whitch we unsubscribe
   */
  unsubscribe(fn) {
    this._listeners = this._listeners.filter((f) => f !== fn);
  }
}

/** Empty dummy state */
const DUMMY_STATE = {};

/**
 * Subscribe to one or more StoreContainers
 * Expect props is:
 * props.to:
 * Array of StoreContainers will create instances automatically
 * or Array of instances of Container's created with new Container.
 * e.g. const storeContainer = new Container;
 *
 * props.children:
 * Function which take (...instances) => returns Node -> (render props)
 * Example:
 *
 export const CounterWrapper = (props) => {
    return (
      <Subscribe to={[CounterContainer]}>
        {counter => <Counter {...counter} {...props} />}
      </Subscribe>
    );
 };
 */
class Subscribe extends Component {
  /** state  */
  state = {};
  /** Array of ContainersType created automatically, or with new Container() */
  instances = [];
  /** flag indicating if container is mounted */
  unmounted = false;

  componentWillUnmount() {
    this.unmounted = true;
    this._unsubscribe();
  }

  _unsubscribe() {
    this.instances.forEach((container) => {
      container.unsubscribe(this.onUpdate);
    });
  }

  onUpdate = () => {
    return new Promise((resolve) => {
      if(!this.unmounted) {
        this.setState(DUMMY_STATE, resolve);
      }
      else {
        resolve();
      }
    });
  };

  /**
   * Create instance of Container if doesnt exists
   *
   * @param {Map | null} map javascrtip Map of ContainerMapType
   * @param {*} containers ContainersType
   *
   * @return Array of ContainerType
   */
  _createInstances(map: null, containers) {
    this._unsubscribe();

    if(map === null) {
      throw new Error(
        'You must wrap your <Subscribe> components with a <Provider>',
      );
    }

    let safeMap = map;
    let instances = containers.map((ContainerItem) => {
      let instance;

      if(
        typeof ContainerItem === 'object' &&
        ContainerItem instanceof Container
      ) {
        instance = ContainerItem;
      }
      else {
        instance = safeMap.get(ContainerItem);

        if(!instance) {
          instance = new ContainerItem();
          safeMap.set(ContainerItem, instance);
        }
      }

      instance.unsubscribe(this.onUpdate);
      instance.subscribe(this.onUpdate);

      return instance;
    });

    this.instances = instances;
    return instances;
  }

  render() {
    return (
      <StoreContext.Consumer>
        {(map) =>
          this.props.children.apply(
            null,
            this._createInstances(map, this.props.to),
          )
        }
      </StoreContext.Consumer>
    );
  }
}

/**
 * Store provider takes two properties.
 * props.children
 * props.inject of type Array of ContainersType e.g. ConterContainer
 *
 * @param {Object} props
 */
function StoreProvider(props) {
  return (
    <StoreContext.Consumer>
      {(parentMap) => {
        let childMap = new Map(parentMap);

        if(props.inject) {
          props.inject.forEach((instance) => {
            childMap.set(instance.constructor, instance);
          });
        }

        return (
          <StoreContext.Provider value={childMap}>
            {props.children}
          </StoreContext.Provider>
        );
      }}
    </StoreContext.Consumer>
  );
}

// export type ProviderProps = {
//   inject?: Array<ContainerType>,
//   children: Node
// };
// TODO: define propTypes

StoreProvider.propTypes = {
  inject: PropTypes.array,
  children: PropTypes.node,
};

/**
 * Simple HOC to connect Component directly to provider (store).
 *
 * @param {React.Component} Component React Component
 * @param {Object} props React props
 * @param {Array} inject inject Containers data directly - dependency injection
 */
const connectToStore = (Component) => (props, inject) => (
  <StoreProvider inject={inject}>
    <Component {...props} />
  </StoreProvider>
);

/**
 * Example HOC to subscripe to store (Containers)
 * TODO: This don't have to be implemented right way, needs test.
 *
 * @param {React.Component} Component React Component
 * @param {Object} props React props
 * @param {Array} subscriptions Array of Containers (store) to subscripe
 */
const view = (Component) => (props, subscriptions) => (
  <Subscribe to={subscriptions}>
    {(...subscriptions) => <Component {...subscriptions} {...props} />}
  </Subscribe>
);

export { Container, Subscribe, StoreProvider, connectToStore, view };



// WEBPACK FOOTER //
// ./src/store/store.js